import React, { useState, useRef } from 'react';
import { FiUpload, FiCheck, FiRefreshCw, FiDownload } from 'react-icons/fi';
// Helper function to sanitize location strings for URLs
const sanitizeLocationForUrl = (location) => {
  // Convert to lowercase
  let sanitized = location.toLowerCase();
  
  // Replace Spanish special characters
  const replacements = {
    'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u',
    'ü': 'u', 'ñ': 'n', 'ç': 'c'
  };
  
  Object.keys(replacements).forEach(char => {
    sanitized = sanitized.replace(new RegExp(char, 'g'), replacements[char]);
  });
  
  // Replace spaces with hyphens and remove any non-alphanumeric characters except hyphens
  sanitized = sanitized.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  
  return sanitized;
};

function SeoMassGenerator() {
  const [keywordToReplace, setKeywordToReplace] = useState('');
  const [customLocations, setCustomLocations] = useState([]);
  const [csvData, setCsvData] = useState(null);
  const [processedData, setProcessedData] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLocationsFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const locations = content.split(/\r?\n/).filter(loc => loc.trim() !== '');
      setCustomLocations(locations);
    };
    reader.readAsText(file);
  };

  const getLocationStats = () => {
    if (customLocations.length === 0) {
      return "No se ha cargado ningún archivo de ubicaciones";
    }
    return `${customLocations.length} ubicaciones cargadas`;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.csv')) {
      processFile(file);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      // Split respecting quoted fields that may contain newlines
      const rows = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < content.length; i++) {
        const ch = content[i];
        if (ch === '"') {
          inQuotes = !inQuotes;
          current += ch;
        } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
          if (current.trim()) rows.push(current);
          current = '';
          if (ch === '\r' && content[i+1] === '\n') i++;
        } else {
          current += ch;
        }
      }
      if (current.trim()) rows.push(current);
      
      // Parse each row respecting quoted fields
      const parsedData = rows.map(row => {
        const cells = [];
        let cell = '';
        let q = false;
        for (let i = 0; i < row.length; i++) {
          const ch = row[i];
          if (ch === '"') {
            if (q && row[i+1] === '"') { cell += '"'; i++; }
            else q = !q;
          } else if (ch === ',' && !q) {
            cells.push(cell);
            cell = '';
          } else {
            cell += ch;
          }
        }
        cells.push(cell);
        return cells;
      });
      setCsvData(parsedData);
    };
    reader.readAsText(file);
  };

  const handleProcessCSV = () => {
    if (!csvData || !keywordToReplace || customLocations.length === 0) return;
    
    setLoading(true);
    try {
      const result = processCSV(csvData, keywordToReplace, customLocations);
      setProcessedData(result);

      // Auto-download
      const csvContent = result.map(row => 
        row.map(cell => {
          if (cell && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
            return '"' + cell.replace(/"/g, '""') + '"';
          }
          return cell || '';
        }).join(',')
      ).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `contenido_seo_${new Date().getTime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error processing CSV:", error);
    } finally {
      setLoading(false);
    }
  };

  const processCSV = (data, keyword, locations) => {
    if (!data || data.length === 0) return null;
    
    const headers = data[0];
    const rows = data.slice(1);
    
    // Permalink is the last column
    const permalinkIndex = headers.length - 1;
    
    // Identify URL columns (columns that contain URLs)
    const urlColumnIndexes = headers.map((header, index) => {
      // Check if any of the first few rows contain URL patterns
      const hasUrls = rows.slice(0, Math.min(5, rows.length)).some(row => {
        return row[index] && (
          row[index].includes('http://') || 
          row[index].includes('https://') ||
          row[index].includes('www.')
        );
      });
      return hasUrls ? index : null;
    }).filter(index => index !== null);
    
    const newRows = [];
    locations.forEach(location => {
      const sanitizedLocation = sanitizeLocationForUrl(location);
      
      rows.forEach(row => {
        const newRow = row.map((cell, columnIndex) => {
          // Permalink column: replace entire content with sanitized location
          if (columnIndex === permalinkIndex) {
            return sanitizedLocation;
          }
          // Check if this is a URL column and contains the keyword
          if (urlColumnIndexes.includes(columnIndex) && cell.toLowerCase().includes(keyword.toLowerCase())) {
            // For URL columns, preserve the original URL structure but replace the keyword with sanitized location
            const regex = new RegExp(keyword.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
            return cell.replace(regex, sanitizedLocation);
          } else {
            // For non-URL columns, implement case-insensitive replacement
            if (!cell) return cell; // Skip empty cells
            
            // Create a case-insensitive regular expression with the keyword
            const regex = new RegExp(keyword.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
            
            // Replace all instances of the keyword with the location, preserving the case pattern where possible
            return cell.replace(regex, match => {
              // If the original is all uppercase, make the replacement all uppercase
              if (match === match.toUpperCase()) {
                return location.toUpperCase();
              }
              // If the original is capitalized, capitalize the replacement
              else if (match.charAt(0) === match.charAt(0).toUpperCase()) {
                return location.charAt(0).toUpperCase() + location.slice(1);
              }
              // Otherwise use the location as is
              else {
                return location;
              }
            });
          }
        });
        newRows.push(newRow);
      });
    });
    
    return [headers, ...newRows];
  };  // Closing the processCSV function

  return (
    <div className="py-8 px-4 bg-[#1A1A1A] text-white min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-center">
        Generador de contenido SEO por palabras claves
      </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* First column */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Palabra clave a reemplazar
          </label>
          <input
            type="text"
            value={keywordToReplace}
            onChange={(e) => setKeywordToReplace(e.target.value)}
            className="w-full px-3 py-2 bg-[#2a2a2a] text-white rounded-md border border-[#3a3a3a] focus:border-[#ff1b6d] focus:ring-1 focus:ring-[#ff1b6d] focus:outline-none"
            placeholder="Ej: Toledo"
          />
          <p className="text-xs text-gray-400">
            Esta palabra será reemplazada por cada ubicación de la lista
          </p>
        </div>
        
        {/* Second column */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-300">
            Lista de ubicaciones
          </label>
          <label 
            htmlFor="locationsFile"
            className="flex items-center justify-center gap-2 px-3 py-2 bg-[#2a2a2a] text-white rounded-md border border-[#3a3a3a] cursor-pointer hover:bg-[#333] transition-colors"
          >
            <FiUpload className="w-4 h-4 text-[#ff1b6d]" />
            Subir archivo TXT con ubicaciones
          </label>
          <input
            id="locationsFile"
            type="file"
            accept=".txt"
            onChange={handleLocationsFileUpload}
            className="hidden"
          />
          <p className="text-xs text-gray-400">
            {getLocationStats()}
          </p>
        </div>
      </div>
      
      <div 
        className={`p-8 text-center border-2 border-dashed rounded-lg mb-6 transition-colors duration-300 
        ${isDragging 
          ? 'border-[#ff1b6d]/80 bg-[#2a2a2a]' 
          : 'border-gray-600 hover:border-[#ff1b6d]/80 hover:bg-[#2a2a2a]'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="flex flex-col items-center justify-center">
          <FiUpload className="text-5xl text-[#ff1b6d] mb-3" />
          <h3 className="text-xl font-medium mb-2">
            Arrastra y suelta tu archivo CSV aquí
          </h3>
          <p className="text-gray-400">
            O haz clic para seleccionar el archivo
          </p>
          <label 
            htmlFor="csvFileInput" 
            className="mt-4 inline-flex items-center px-4 py-2 bg-[#2a2a2a] text-white rounded-md 
            hover:bg-[#333] transition-colors cursor-pointer border border-[#3a3a3a]"
          >
            Seleccionar archivo CSV
          </label>
          <input
            id="csvFileInput"
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>

      {csvData && (
        <div className="text-green-400 text-center mb-6 flex items-center justify-center gap-2">
          <FiCheck className="w-5 h-5" />
          Archivo CSV cargado correctamente ({csvData.length} filas)
        </div>
      )}

      <div className="flex justify-center mb-8">
        <button 
          className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200
          ${(!csvData || !keywordToReplace || !customLocations.length)
            ? 'bg-[#2a2a2a] text-gray-500 cursor-not-allowed' 
            : 'bg-[#ff1b6d] text-white hover:bg-[#e61060] shadow-lg hover:shadow-[#ff1b6d]/25'}`}
          onClick={handleProcessCSV} 
          disabled={!csvData || !keywordToReplace || !customLocations.length || loading}
        >
          {loading ? (
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
          ) : (
            <>
              <FiDownload className="w-5 h-5" />
              <span>Procesar y Descargar CSV</span>
            </>
          )}
        </button>
      </div>

      {/* Instrucciones */}
      <div className="bg-[#2a2a2a] rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-[#ff1b6d]">Instrucciones de uso</h2>
        <ol className="list-decimal list-inside space-y-2 text-gray-300">
          <li>Introduce la palabra clave que quieres reemplazar en los textos</li>
          <li>Sube un archivo TXT con las ubicaciones (una por línea)</li>
          <li>Sube tu archivo CSV con el contenido para optimizar</li>
          <li>Haz clic en "Procesar y Descargar CSV" para generar y descargar las versiones para cada ubicación</li>
        </ol>
      </div>
    </div>
  );
};

export default SeoMassGenerator;

